<script setup>
import { storeToRefs } from "pinia";
import { computed } from "vue";
import { useRoute } from "vue-router";

import { getFeatureQuery } from "@/feature";
import { useAppStore } from "@/stores";
import { daysUntil, expiryText } from "@/utils/dates";
import { capitalizeFirstLetter } from "@/utils/text";

const route = useRoute();

const { isAdmin, isTraining, license, licenseUsedCount, onPrem } = storeToRefs(useAppStore());

const expireDays = () => daysUntil(license.value.expiryDate);
const expiring = () => expireDays() !== null && expireDays() < 30;
const licenseTotal = computed(() => license.value?.numberOfTeams ?? null);
const planName = computed(() =>
  license.value?.plan ? `${capitalizeFirstLetter(license.value.plan)} plan` : "",
);

// Paid-plan card: trial admins get TrialExtensionCard instead.
const showCard = computed(
  () =>
    isAdmin.value &&
    !isTraining.value &&
    license.value?.type !== "trial" &&
    licenseUsedCount.value !== null &&
    licenseTotal.value !== null &&
    licenseTotal.value > 0,
);

const usagePercent = computed(() => {
  if (!licenseTotal.value || licenseUsedCount.value === null) return 0;
  return Math.min(100, Math.round((licenseUsedCount.value / licenseTotal.value) * 100));
});

function query() {
  return getFeatureQuery(route);
}
</script>

<template>
  <router-link
    v-if="showCard"
    :to="{ name: 'billing', query: query() }"
    class="usage-card"
    :class="{ 'usage-card-warning': expiring() }"
  >
    <div v-if="expiring() && !onPrem" class="license-warning">
      <span>
        {{ expiryText(expireDays()) }}
      </span>
      <span class="license-warning-cta"> {{ expireDays() <= 0 ? "Renew" : "Extend" }} → </span>
    </div>
    <p class="usage-eyebrow">{{ planName }}</p>
    <p class="usage-count">
      <span class="usage-used">{{ licenseUsedCount }}</span>
      <span class="usage-of">/ {{ licenseTotal }} teams</span>
    </p>
    <div class="usage-track" :aria-label="`${usagePercent}% of team licenses used`">
      <div
        class="usage-fill"
        :class="{ 'usage-fill-warning': expiring() }"
        :style="{ width: `${usagePercent}%` }"
      />
    </div>
  </router-link>
</template>

<style lang="scss" scoped>
@use "@/colors" as c;
@use "@/styles/palette" as p;

.usage-card {
  display: block;
  text-decoration: none;
  background-color: c.$background-ui;
  border: 1px solid c.$border-subtle;
  border-radius: 8px;
  overflow: hidden;
  transition:
    background-color 120ms ease,
    border-color 120ms ease;

  &:hover {
    background-color: p.$black-alpha-5;
    border-color: c.$border;
  }

  &.usage-card-warning {
    border-color: c.$destructive;
  }

  .license-warning {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 8px 14px;
    background-color: c.$destructive;
    color: c.$text-inverse;
    font-size: 12px;
    font-weight: 500;
  }

  .license-warning-cta {
    font-weight: 700;
    white-space: nowrap;
    flex-shrink: 0;
  }

  .usage-eyebrow {
    padding: 12px 14px 0;
    margin: 0 0 6px;
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: c.$text-subtle;
  }

  .usage-count {
    padding: 0 14px;
    display: flex;
    align-items: baseline;
    gap: 6px;
    margin: 0 0 8px;
    color: c.$main;

    .usage-used {
      font-size: 18px;
      font-weight: 600;
      letter-spacing: -0.01em;
    }

    .usage-of {
      font-size: 12px;
      font-weight: 500;
      color: c.$text-subtle;
    }
  }

  .usage-track {
    margin: 0 14px 12px;
    height: 4px;
    background-color: c.$border-subtle;
    border-radius: 999px;
    overflow: hidden;

    .usage-fill {
      height: 100%;
      background-color: c.$main;
      border-radius: 999px;
      transition: width 200ms ease;

      &.usage-fill-warning {
        background-color: c.$destructive;
      }
    }
  }
}
</style>
